import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

import { ReleaseHighlightsContent } from '@qiasphere/interfaces';

import { GenericDialogComponent } from '../../dialogs/generic-dialog/generic-dialog.component';
import { GenericSnackbarComponent } from '../../dialogs/generic-snackbar/generic-snackbar.component';
import { RefreshPageDialogComponent } from '../../dialogs/refresh-page-dialog/refresh-page-dialog.component';
import { ReleaseHighlightsDialogComponent } from '../../dialogs/release-highlights/release-highlights-dialog.component';
import { ConfirmButtonsText, LinkButton } from '../interfaces/dialog';

/**
 * Centralizes all user-facing feedback (snackbars, confirmations, error
 * dialogs) so that look-and-feel and UX patterns are consistent across
 * the entire application.
 *
 * Separated from individual components because dialog/snackbar configuration
 * (duration, position, panel classes, default messages) is a cross-cutting
 * concern. Callers only need to specify the variable parts (title, message).
 *
 * Important: all methods return Promises that resolve when the dialog
 * closes, allowing callers to chain post-confirmation logic.
 */
const SNACKBAR_DEBUG_DURATION_MS = 10_000;
const SNACKBAR_SUCCESS_DURATION_MS = 5_000;
const DEFAULT_DIALOG_WIDTH = '600px';

@Injectable({ providedIn: 'root' })
export class DialogService {
  dialog = inject(MatDialog);
  private readonly snackbar = inject(MatSnackBar);

  /**
   * Shows a long-duration (10s) snackbar at the bottom-right, intended
   * for surfacing backend errors during development without blocking the UI.
   */
  debug_error(title: string, message?: string): Promise<any> {
    return firstValueFrom(
      this.snackbar
        .openFromComponent(GenericSnackbarComponent, {
          data: { title, message, icon: 'check_circle_outline' },
          duration: SNACKBAR_DEBUG_DURATION_MS,
          verticalPosition: 'bottom',
          horizontalPosition: 'right',
          panelClass: ['snackbar_error']
        })
        .afterDismissed()
    );
  }
  success(title: string, message?: string): Promise<any> {
    return firstValueFrom(
      this.snackbar
        .openFromComponent(GenericSnackbarComponent, {
          data: { title, message, icon: 'check_circle_outline' },
          duration: SNACKBAR_SUCCESS_DURATION_MS,
          verticalPosition: 'top',
          panelClass: ['snackbar_success']
        })
        .afterDismissed()
    );
  }

  error(title?: string, message?: string, buttonText?: string): Promise<any> {
    const dialogRef = this.dialog.open(GenericDialogComponent, {
      data: {
        title: title || 'An error occurred while fetching data from server.',
        message: this.escapeHtml(message) || 'Try refreshing the page.',
        icon: 'highlight_off',
        confirmButtonText: buttonText || 'Close'
      },
      panelClass: ['error']
    });
    return firstValueFrom(dialogRef.afterClosed().pipe(map(e => ({ value: e }))));
  }

  /**
   * Displays a non-dismissable error overlay for unrecoverable failures
   * (e.g. missing config, expired session). The user can only proceed
   * via the optional link button (typically "Go to login").
   */
  fatalError(title?: string, message?: string, linkButton?: LinkButton, customIcon?: string): Promise<any> {
    const dialogRef = this.dialog.open(GenericDialogComponent, {
      width: DEFAULT_DIALOG_WIDTH,
      data: {
        title: title || 'An error occurred while fetching data from server.',
        message: message || 'Try refreshing the page.',
        icon: customIcon || 'highlight_off',
        linkButton
      },
      panelClass: ['error'],
      disableClose: true
    });
    return firstValueFrom(dialogRef.afterClosed().pipe(map(e => ({ value: e }))));
  }

  confirm(message?: string, confirmButtonsText?: ConfirmButtonsText, title = 'Are you sure?'): Promise<any> {
    const dialogRef = this.dialog.open(GenericDialogComponent, {
      width: DEFAULT_DIALOG_WIDTH,
      data: {
        title,
        message,
        cancelButtonText: confirmButtonsText ? confirmButtonsText.cancelButtonText : 'No',
        confirmButtonText: confirmButtonsText ? confirmButtonsText.confirmButtonText : 'Yes'
      },
      panelClass: ['confirm']
    });
    return firstValueFrom(dialogRef.afterClosed().pipe(map(e => ({ value: e }))));
  }

  info(message?: string): Promise<any> {
    const dialogRef = this.dialog.open(GenericDialogComponent, {
      width: DEFAULT_DIALOG_WIDTH,
      data: {
        title: 'Information',
        message,
        confirmButtonText: 'Ok'
      },
      panelClass: ['confirm']
    });
    return firstValueFrom(dialogRef.afterClosed().pipe(map(e => ({ value: e }))));
  }

  releaseHighlights(content: ReleaseHighlightsContent): Promise<any> {
    const dialogRef = this.dialog.open(ReleaseHighlightsDialogComponent, {
      width: DEFAULT_DIALOG_WIDTH,
      data: content,
      panelClass: ['confirm']
    });
    return firstValueFrom(dialogRef.afterClosed().pipe(map(e => ({ value: e }))));
  }

  /**
   * Escapes HTML-significant characters in user-supplied strings so they
   * can be safely rendered via [innerHTML] bindings in dialog templates.
   * Needed because DialogService.error() shows backend error messages
   * that may contain untrusted content (e.g. HTTP error responses).
   */
  private escapeHtml(text: string): string {
    if (!text) return text;
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /**
   * Displays a non-dismissable dialog that forces the user to refresh
   * the browser, used when a WebSocket reconnect or version mismatch
   * makes the current page state unreliable.
   */
  refreshPage(message: string): Promise<any> {
    const dialogRef = this.dialog.open(RefreshPageDialogComponent, {
      data: {
        title: 'Error',
        message: message,
        icon: 'highlight_off',
        confirmButtonText: 'Dismiss'
      },
      panelClass: ['error'],
      disableClose: true
    });
    return firstValueFrom(dialogRef.afterClosed().pipe(map(e => ({ value: e }))));
  }
}
